/* eslint-disable camelcase */
import React, { useMemo, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Row, Col } from 'react-bootstrap';
import { StylishNewButton } from '../../../DesignSystems/New/StylishNewButton';
import DrawerWrapper, { DrawerFooter } from '../../DrawerWrapper';
import FormTextField from '../../formFields/FormTextField';
import FormTextAreaField from '../../formFields/FormTextAreaField';
import FormDateTimeField from '../../formFields/FormDateTimeField';
import FormSelectField from '../../formFields/FormSelectField';
import FormGeoLocationField from '../../formFields/FormGeoLocationField';
import { ResourceInIncident } from '../../hooks/resourceInIncidentHooks';
import { useResourceSites } from '../../hooks/resourceSiteHooks';
import { useIncident, usePeriods } from '../../hooks/incidentHooks';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../slices/commonSelectors';
import { Form } from 'react-bootstrap';
import FormNumericField from '../../formFields/FormNumericField';
import { useIncidentRoster } from '../../hooks/incidentRoster/useIncidentRoster';
import { ActionAttachmentsSection } from '../../actionTicket/ActionAttachmentsSection';
import type { ActionAttachment } from '../../actionTicket/actionTicketModel';
import { useActualIncidentId } from '../../hooks/commonHooks';

interface CheckInDrawerProps {
  resource: Partial<ResourceInIncident>;
  onClose: () => void;
  onSave: (data: Partial<ResourceInIncident>) => void;
  isUpdate?: boolean;
  /**
   * Check in something that is not on the organization's asset list — a mutual aid crew, a walk-up volunteer, a
   * contractor's truck. Name, kind and type become the recorder's to type, since there is no asset record to read
   * them off, and the save creates one.
   */
  isWalkUp?: boolean;
}

interface CheckInFormData {
  // Resource classification
  stateAndAgency?: string;
  resourceCategory?: string;
  resourceKind?: string;
  resourceType?: string;
  resourceNameIdentifier?: string;
  strikeTeamTaskForce?: string;
  // Resource details
  orderRequestNumber?: string;
  leaderName: string;
  personnelCount: number | null;
  incidentContactInfo?: string;
  homeUnitAgency?: string;
  useCustomDeparture?: boolean;
  departureSiteId?: string;
  departureGeolocation?: {
    lat?: number;
    lng?: number;
    name?: string;
    address?: string;
  } | null;
  departureDatetime?: string;
  travelMethod?: string;
  useCustomAssignment?: boolean;
  assignmentSiteId?: string;
  assignmentGeolocation?: {
    lat?: number;
    lng?: number;
    name?: string;
    address?: string;
  } | null;
  otherQualifications?: string;
  // Data transmission
  dataSentToReslDatetime?: string;
  dataSentToReslInitials?: string;
  preparedByName?: string;
  preparedByPosition?: string;
}
const catOptions: { value: string; label: string }[] = [
  { value: 'FF', label: 'FF- Firefighting' },
  { value: 'SK', label: 'SK- Skimmer' },
  { value: 'AM', label: 'AM- Ambulance' },
  { value: 'LE', label: 'LE- Law Enforcement' },
  { value: 'VT', label: 'VT- Vacuum Truck' },
  { value: 'CB', label: 'CB- Crew Boat' },
  { value: 'DB', label: 'DB- Deck Barge' },
  { value: 'OWB', label: 'OWB- Oil Work Boat' },
  { value: 'SAR', label: 'SAR- Search And Rescue' },
  { value: 'TV', label: 'TV- Tank Vessel' },
  { value: 'HAZ', label: 'HAZ- Hazmat' },
  { value: 'SCAT', label: 'SCAT- Shoreline Cleanup Assessment Team' },
  { value: 'SERT', label: 'SERT- Salvage Engineering Response Team' },
];

const CheckInDrawer: React.FC<CheckInDrawerProps> = ({
  resource,
  onClose,
  onSave,
  isUpdate = false,
  isWalkUp = false,
}) => {
  const { data: resourceSites = [] } = useResourceSites();
  const { data: incident } = useIncident();
  const { data: periods = [] } = usePeriods();
  const { data: incidentRoster = [] } = useIncidentRoster();
  const currentUser = useSelector(selectUser);
  const incidentId = useActualIncidentId() ?? resource.incident_id;
  const [attachments, setAttachments] = useState<ActionAttachment[]>(
    resource.attachments ?? []
  );

  // Get current operational period
  const currentPeriod = React.useMemo(() => {
    if (!incident?.current_period || !periods.length) return null;
    return periods.find((p) => p.id === incident.current_period);
  }, [incident, periods]);

  // Get user's incident seat
  const userIncidentSeats = React.useMemo(() => {
    if (!currentUser?.user_guid || !incidentRoster.length) return '';
    const userEntry = incidentRoster.find(
      (member: {
        userId: string;
        incidentId: string;
        active: number | null;
        incidentPositions: { positionId: string; positionName: string }[];
      }) =>
        member.userId === currentUser.user_guid &&
        member.incidentId === incident?.id &&
        member.active === 1
    );
    return (
      userEntry?.incidentPositions
        .map((position) => position.positionName)
        .join(', ') || ''
    );
  }, [currentUser, incidentRoster, incident]);

  const form = useForm<CheckInFormData>({
    mode: 'onChange',
    defaultValues: {
      // Resource classification
      stateAndAgency: resource.state_and_agency || '',
      resourceCategory: resource.resource_category || '',
      resourceKind: '', // These should be ICS short codes, not full descriptions
      resourceType: '', // These should be ICS short codes, not full descriptions
      resourceNameIdentifier:
        resource.resource_name || resource.resource_name_identifier || '',
      strikeTeamTaskForce: resource.strike_team_task_force || '',
      // Resource details
      orderRequestNumber: resource.order_request_number || '',
      leaderName: resource.leader_name || '',
      personnelCount: isUpdate ? (resource.personnel_count ?? null) : null,
      incidentContactInfo: resource.incident_contact_info || '',
      homeUnitAgency: resource.home_unit_agency || '',
      useCustomDeparture: !!resource.departure_geolocation,
      departureSiteId: resource.departure_site_id || '',
      departureGeolocation: resource.departure_geolocation || null,
      departureDatetime: resource.departure_datetime || '',
      travelMethod: resource.travel_method || '',
      useCustomAssignment: !!resource.assignment_geolocation,
      assignmentSiteId: resource.assignment_site_id || '',
      assignmentGeolocation: resource.assignment_geolocation || null,
      otherQualifications: resource.other_qualifications || '',
      // Data transmission
      dataSentToReslDatetime: resource.data_sent_to_resl_datetime || '',
      dataSentToReslInitials: resource.data_sent_to_resl_initials || '',
      preparedByName: isUpdate
        ? resource.prepared_by_name || currentUser?.user_name || ''
        : currentUser?.user_name || '',
      preparedByPosition: isUpdate
        ? resource.prepared_by_position || userIncidentSeats
        : userIncidentSeats,
    },
  });

  const { handleSubmit, watch, reset } = form;
  const useCustomDeparture = watch('useCustomDeparture');
  const useCustomAssignment = watch('useCustomAssignment');

  // Reset form when resource changes (for update mode)
  useEffect(() => {
    if (isUpdate && resource) {
      reset({
        // Resource classification
        stateAndAgency: resource.state_and_agency || '',
        resourceCategory: resource.resource_category || '',
        resourceKind: '', // These should be ICS short codes, not full descriptions
        resourceType: '', // These should be ICS short codes, not full descriptions
        resourceNameIdentifier:
          resource.resource_name || resource.resource_name_identifier || '',
        strikeTeamTaskForce: resource.strike_team_task_force || '',
        // Resource details
        orderRequestNumber: resource.order_request_number || '',
        leaderName: resource.leader_name || '',
        personnelCount: resource.personnel_count ?? null,
        incidentContactInfo: resource.incident_contact_info || '',
        homeUnitAgency: resource.home_unit_agency || '',
        useCustomDeparture: !!resource.departure_geolocation,
        departureSiteId: resource.departure_site_id || '',
        departureGeolocation: resource.departure_geolocation || null,
        departureDatetime: resource.departure_datetime || '',
        travelMethod: resource.travel_method || '',
        useCustomAssignment: !!resource.assignment_geolocation,
        assignmentSiteId: resource.assignment_site_id || '',
        assignmentGeolocation: resource.assignment_geolocation || null,
        otherQualifications: resource.other_qualifications || '',
        // Data transmission
        dataSentToReslDatetime: resource.data_sent_to_resl_datetime || '',
        dataSentToReslInitials: resource.data_sent_to_resl_initials || '',
        preparedByName:
          resource.prepared_by_name || currentUser?.user_name || '',
        preparedByPosition: resource.prepared_by_position || userIncidentSeats,
      });
    }
  }, [isUpdate, resource, reset, currentUser, userIncidentSeats]);

  const resourceSiteOptions = useMemo(
    () =>
      resourceSites.map((site) => ({
        value: site.id,
        label: site.name,
      })),
    [resourceSites]
  );

  const onSubmit = (data: CheckInFormData) => {
    const checkinData: Partial<ResourceInIncident> = {
      state_and_agency: data.stateAndAgency,
      resource_category: data.resourceCategory,
      resource_kind: data.resourceKind,
      resource_type: data.resourceType,
      // A walk-up has no asset record to read a name off, so the recorder's entry is the name.
      resource_name_identifier: isWalkUp
        ? data.resourceNameIdentifier
        : resource.resource_name || '',
      strike_team_task_force: data.strikeTeamTaskForce,
      order_request_number: data.orderRequestNumber,
      leader_name: data.leaderName,
      personnel_count:
        data.personnelCount === null
          ? 0
          : parseInt(String(data.personnelCount), 10) || 0,
      incident_contact_info: data.incidentContactInfo,
      home_unit_agency: data.homeUnitAgency,
      departure_site_id: data.departureSiteId,
      departure_geolocation: data.departureGeolocation,
      departure_datetime: data.departureDatetime,
      travel_method: data.travelMethod,
      assignment_site_id: data.assignmentSiteId,
      assignment_geolocation: data.assignmentGeolocation,
      other_qualifications: data.otherQualifications,
      data_sent_to_resl_datetime: data.dataSentToReslDatetime,
      data_sent_to_resl_initials: data.dataSentToReslInitials,
      prepared_by_name: data.preparedByName,
      prepared_by_position: data.preparedByPosition,
      prepared_datetime: new Date().toISOString(),
      attachments,
      // Auto-populate operational period from incident
      operational_period_from: currentPeriod?.start_date_time,
      operational_period_to: currentPeriod?.end_date_time,
    };

    // Remove empty ICS classification fields - these come from resource definition
    if (!checkinData.resource_kind) delete checkinData.resource_kind;
    if (!checkinData.resource_type) delete checkinData.resource_type;

    // Only set checkin_datetime for new check-ins, not updates
    if (!isUpdate) {
      checkinData.checkin_datetime = new Date().toISOString();
    }

    // Handle departure point based on toggle
    if (data.useCustomDeparture && data.departureGeolocation) {
      checkinData.departure_geolocation = data.departureGeolocation;
    } else if (data.departureSiteId) {
      const selectedSite = resourceSites.find(
        (site) => site.id === data.departureSiteId
      );
      checkinData.departure_point = selectedSite?.name || '';
      checkinData.departure_site_id = data.departureSiteId;
    }

    // Handle assignment location based on toggle
    if (data.useCustomAssignment && data.assignmentGeolocation) {
      checkinData.assignment_geolocation = data.assignmentGeolocation;
      checkinData.incident_assignment_location =
        data.assignmentGeolocation.name ||
        data.assignmentGeolocation.address ||
        'Custom Location';
    } else if (data.assignmentSiteId) {
      const selectedSite = resourceSites.find(
        (site) => site.id === data.assignmentSiteId
      );
      checkinData.incident_assignment_location = selectedSite?.name || '';
      checkinData.assignment_site_id = data.assignmentSiteId;
    }

    // Remove any undefined fields
    Object.keys(checkinData).forEach((key) => {
      if (checkinData[key as keyof ResourceInIncident] === undefined) {
        delete checkinData[key as keyof ResourceInIncident];
      }
    });

    onSave(checkinData);
  };

  return (
    <DrawerWrapper
      toggle={onClose}
      title={
        isUpdate
          ? 'Update ICS 211 Check-In'
          : isWalkUp
            ? 'ICS 211 Walk-Up Check-In'
            : 'ICS 211 Check-In Form'
      }
    >
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-4">
            {isUpdate && resource.checkin_datetime && (
              <Row className="mb-4">
                <Col md={12}>
                  <label className="form-label">
                    Initial Check-In Date/Time
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={new Date(resource.checkin_datetime).toLocaleString(
                      'en-US',
                      {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      }
                    )}
                    disabled
                    readOnly
                  />
                </Col>
              </Row>
            )}
            <Row className="mb-2">
              <strong>Resource Information</strong>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                {isWalkUp ? (
                  <FormTextField
                    fieldName="resourceNameIdentifier"
                    title="Resource Name or Identifier"
                    rules={{ required: 'A name or identifier is required' }}
                  />
                ) : (
                  <div className="mt-2">
                    <label className="form-label">Resource Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={resource.resource_name || '-'}
                      disabled
                      readOnly
                    />
                  </div>
                )}
              </Col>
              <Col md={6}>
                <FormTextField
                  fieldName="stateAndAgency"
                  title="Home State and Agency"
                />
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                <FormSelectField
                  fieldName="resourceCategory"
                  title="Category (CAT)"
                  options={catOptions}
                  isClearable
                />
              </Col>
              <Col md={6}>
                {isWalkUp ? (
                  <FormTextField fieldName="resourceKind" title="Kind" />
                ) : (
                  <div className="mt-2">
                    <label className="form-label">Resource Kind</label>
                    <input
                      type="text"
                      className="form-control"
                      value={
                        resource.resource_kind ||
                        resource.resource_set_kind ||
                        '-'
                      }
                      disabled
                      readOnly
                    />
                  </div>
                )}
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                {isWalkUp ? (
                  <FormTextField fieldName="resourceType" title="Type" />
                ) : (
                  <div className="mt-2">
                    <label className="form-label">Resource Type</label>
                    <input
                      type="text"
                      className="form-control"
                      value={
                        resource.resource_type ||
                        resource.resource_set_type ||
                        '-'
                      }
                      disabled
                      readOnly
                    />
                  </div>
                )}
              </Col>
              <Col md={6}>
                <FormSelectField
                  fieldName="strikeTeamTaskForce"
                  title="ST/TF"
                  options={[
                    { value: 'ST', label: 'ST - Strike Team' },
                    { value: 'TF', label: 'TF - Task Force' },
                  ]}
                  isClearable
                />
              </Col>
            </Row>
            <div className="mb-2">
              <strong>Check-In Information</strong>
            </div>
            <Row className="mb-2">
              <Col md={6}>
                <FormTextField
                  fieldName="orderRequestNumber"
                  title="Order Request #"
                />
              </Col>
              <Col md={6}>
                <FormTextField fieldName="leaderName" title="Leader Name" />
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                <FormNumericField
                  fieldName="personnelCount"
                  title="Total # of Personnel"
                  min={0}
                  step={1}
                  rules={{
                    min: { value: 0, message: 'Cannot be negative' },
                    pattern: {
                      value: /^\d+$/,
                      message: 'Must be a whole number',
                    },
                  }}
                />
              </Col>
              <Col md={6}>
                <FormTextField
                  fieldName="incidentContactInfo"
                  title="Incident Contact Information"
                />
              </Col>
            </Row>

            <Row className="mb-2">
              <Col md={6}>
                <FormTextField
                  fieldName="homeUnitAgency"
                  title="Home (Base) Unit or Agency"
                />
              </Col>
              <Col md={6}>
                <FormTextField
                  fieldName="travelMethod"
                  title="Method of Travel"
                />
              </Col>
            </Row>

            <Row className="mb-2">
              <Col md={12}>
                <Form.Check
                  type="switch"
                  id="use-custom-departure"
                  label="Use Custom Departure Location"
                  {...form.register('useCustomDeparture')}
                  className="mb-3"
                />

                {useCustomDeparture ? (
                  <FormGeoLocationField
                    name="departureGeolocation"
                    label="Custom Departure Location"
                  />
                ) : (
                  <FormSelectField
                    fieldName="departureSiteId"
                    title="Departure Site"
                    options={resourceSiteOptions}
                    isClearable
                  />
                )}
              </Col>
            </Row>

            <Row className="mb-2">
              <Col md={6}>
                <FormDateTimeField
                  fieldName="departureDatetime"
                  title="Departure Date/Time"
                />
              </Col>
            </Row>

            <Row className="mb-2">
              <Col md={12}>
                <Form.Check
                  type="switch"
                  id="use-custom-assignment"
                  label="Use Custom Incident Assignment Location"
                  {...form.register('useCustomAssignment')}
                  className="mb-3"
                />

                {useCustomAssignment ? (
                  <FormGeoLocationField
                    name="assignmentGeolocation"
                    label="Custom Assignment Location"
                  />
                ) : (
                  <FormSelectField
                    fieldName="assignmentSiteId"
                    title="Incident Assignment Location"
                    options={resourceSiteOptions}
                    isClearable
                  />
                )}
              </Col>
            </Row>

            <Row className="mb-2">
              <Col md={12}>
                <FormTextAreaField
                  fieldName="otherQualifications"
                  title="Other Qualifications"
                />
              </Col>
            </Row>

            <hr className="my-4" />

            {/* Data Transmission Section */}
            <h6 className="mb-3">Data Transmission</h6>

            <Row className="mb-2">
              <Col md={6}>
                <FormDateTimeField
                  fieldName="dataSentToReslDatetime"
                  title="Data Sent to RESL"
                />
              </Col>
              <Col md={6}>
                <FormTextField
                  fieldName="dataSentToReslInitials"
                  title="Sent by (Initials)"
                />
              </Col>
            </Row>

            <hr />

            <Row className="mb-2">
              <Col md={6}>
                <label className="form-label">Prepared By (Name)</label>
                <input
                  type="text"
                  className="form-control"
                  value={
                    isUpdate
                      ? resource.prepared_by_name ||
                        currentUser?.user_name ||
                        '-'
                      : currentUser?.user_name || '-'
                  }
                  disabled
                  readOnly
                />
              </Col>
              <Col md={6}>
                <label className="form-label">Position</label>
                <input
                  type="text"
                  className="form-control"
                  value={
                    isUpdate
                      ? resource.prepared_by_position ||
                        userIncidentSeats ||
                        '-'
                      : userIncidentSeats || '-'
                  }
                  disabled
                  readOnly
                />
              </Col>
            </Row>
          </div>

          <div className="dark tw:rounded-lg tw:bg-background tw:text-foreground">
            <ActionAttachmentsSection
              incidentId={incidentId}
              value={attachments}
              onChange={setAttachments}
            />
          </div>

          <DrawerFooter>
            <StylishNewButton
              type="button"
              className="button button--link me-2"
              onClick={onClose}
            >
              Cancel
            </StylishNewButton>
            <StylishNewButton type="submit" className="button button--primary">
              {isUpdate
                ? 'Update Check-In'
                : isWalkUp
                  ? 'Create and Check In'
                  : 'Check In Resource'}
            </StylishNewButton>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default CheckInDrawer;
